/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
import { Form, Input } from 'antd';
import { VFC } from 'react';
import { UNIT_TRANSLATED } from '@enmon/commons/lib/shared/unit';
import { OutputTechnology } from '@enmon/commons/lib/tools';
import { ImageUpload, ImageUploadProps } from 'components/ImageUpload/ImageUpload';
import { filterOption, filterSort } from 'components/Select/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import { SerializedMeterRegister } from '@enmon/commons/lib/shared/meter';
import { FormItem } from '../../../../components/Form/FormItem';
import { Select } from '../../../../components/Select/Select';
import { InstallationDetailStepState, InstallationMeterRegisterFormValues } from './slice';
import { RegisterSection as messages } from './messages';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';
import { PulseRate } from '../../../../components/Form/meter_model_suggestions/PulseRate';
import { getChannelOptions } from './utils/getChannelOptions';

const combineRegisterName = ({ name, obis, tariff }: Pick<SerializedMeterRegister, 'name' | 'obis' | 'tariff'>) => {
  let text = name ?? '';
  if (obis) text += ` (${obis}.${tariff ?? 0})`;
  return text;
};

export const RegisterSection: VFC<{
  register: InstallationMeterRegisterFormValues;
  index: number;
}> = ({ register, index }) => {
  const { unit } = register;
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();
  const { programmable_pulses, led_rate } = meterModelSuggestions ?? {};
  const intl = useIntl();
  const form = Form.useFormInstance<InstallationDetailStepState>();
  const mountTechnology = form.getFieldValue(['meterMountTechnology']) as OutputTechnology;

  return (
    <>
      <FormItem hidden initialValue={register._id} name={['registers', index, 'id']}>
        <Input />
      </FormItem>
      <FormItem label={<FormattedMessage {...messages.registerObis} values={{ register: index + 1 }} />}>
        <Input disabled value={combineRegisterName(register)} />
      </FormItem>
      {mountTechnology === OutputTechnology.Impulse && (
        <>
          <FormItem
            extra={programmable_pulses?.value ? <FormattedMessage {...messages.programmablePulsesExtra} /> : undefined}
            help={
              led_rate?.value ? (
                <FormattedMessage {...messages.ledRateHelp} values={{ led_rate: led_rate.value }} />
              ) : undefined
            }
            initialValue={register.factor}
            label={<FormattedMessage {...messages.registerFactorLabel} />}
            name={['registers', index, 'factor']}
            rules={[
              { required: true, message: intl.formatMessage(messages.registerFactorRequired) },
              {
                validator: (_, value: number | undefined) =>
                  (value ?? 0) > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(intl.formatMessage(messages.registerFactorValidationMessage))),
              },
            ]}
          >
            <PulseRate />
          </FormItem>

          <FormItem
            initialValue={register.channel}
            label={<FormattedMessage {...messages.dataSourceChannelLabel} />}
            name={['registers', index, 'channel']}
          >
            <Select
              allowClear
              showSearch
              filterOption={filterOption}
              filterSort={filterSort}
              options={getChannelOptions(intl)}
              placeholder={<FormattedMessage {...messages.dataSourceChannelPlaceholder} />}
            />
          </FormItem>
        </>
      )}
      <FormItem
        initialValue={register.counter}
        label={<FormattedMessage {...messages.registerCounterPlaceholder} />}
        name={['registers', index, 'counter']}
        rules={[{ required: true, message: intl.formatMessage(messages.registerRequired) }]}
      >
        <InputNumber
          addonAfter={unit && UNIT_TRANSLATED[unit]}
          decimalSeparator=","
          precision={3}
          step={0.01}
          style={{ width: '100%' }}
        />
      </FormItem>
      <FormItem
        initialValue={register.counterPhotos}
        label={<FormattedMessage {...messages.registerPhotos} />}
        name={['registers', index, 'counterPhotos']}
        rules={[{ required: true, message: intl.formatMessage(messages.registerPhotosRequired) }]}
        trigger={'onImageUpload' satisfies keyof ImageUploadProps}
        valuePropName={'fileList' satisfies keyof ImageUploadProps}
      >
        <ImageUpload multiple />
      </FormItem>
    </>
  );
};
